import { Form } from "antd";
import { useCallback, useEffect, useState } from "react";

import { AutocategorizationRuleFragment } from "../../../api/graphql/fragments/autocategorizationRule.generated";
import {
  CreateAutocategorizationRuleInput,
  RuleConditionNumberOp,
  RuleConditionStringOp,
} from "../../../api/graphql/schema.generated";
import { Direction } from "../../../types";
import { categoryCodeCodec, vatCategoryCodeCodec } from "../../../utils";
import { recipientVatIdForUpsert } from "../../../utils/recipientVatId";
import {
  encodeFormAmountCondition,
  INVERSE_CONDITION_NUMBER_OP,
} from "./helpers";

export type RuleSplitLineForm = {
  categoryCode: string;
  vatCategoryCode: string;
  amount: number;
};

export const SPLIT_LINE_ERRORS = {
  CATEGORY: "Please choose a category for each split line",
  VAT_CATEGORY: "Please choose a VAT category for each split line",
  AMOUNT: "Each split line must have a non-zero amount",
  MIN_LINES: "At least 2 split lines are required",
};

/**
 * A recipient VAT ID only makes sense when the rule pins exactly one recipient:
 * a counterparty-name condition, or a single IBAN. Two or more IBANs (or no such
 * condition) would let one VAT ID apply across recipients. Mirrors the backend
 * `ruleTargetsSingleRecipient` guard; shared by the field validator and the
 * encode path so both agree.
 */
export function ruleFormTargetsSingleRecipient(conditions: {
  ibans?: (string | null | undefined)[];
  name?: { value?: string | null };
}): boolean {
  const ibans = (conditions.ibans ?? []).filter(Boolean);
  const hasName = Boolean(conditions.name?.value);
  return ibans.length <= 1 && (hasName || ibans.length === 1);
}

export function useRuleForm(config: { rule?: AutocategorizationRuleFragment }) {
  const [form, setForm] = useState(() => getRuleFormState(config.rule));

  const [formControls] = Form.useForm<typeof form>();
  const setFieldsValue = useCallback<(typeof formControls)["setFieldsValue"]>(
    (values) => {
      formControls.setFieldsValue(values);
      setForm(formControls.getFieldsValue(true));
    },
    [formControls]
  );

  useEffect(() => {
    setFieldsValue(getRuleFormState(config.rule));
  }, [config.rule, setFieldsValue]);

  const getRule = useCallback(():
    | CreateAutocategorizationRuleInput
    | string => {
    const amount = form.conditions.direction
      ? encodeFormAmountCondition(
          form.conditions.direction,
          form.conditions.amount
        )
      : undefined;
    if (amount instanceof Error) {
      return amount.message;
    }

    const conditions = {
      accountIds: form.conditions.accountIds?.length
        ? form.conditions.accountIds
        : undefined,
      amount,
      description: form.conditions.description.value
        ? form.conditions.description
        : undefined,
      hasEmployees: form.conditions.hasEmployees ?? undefined,
      hasInvoices: form.conditions.hasInvoices ?? undefined,
      hasVatNumber: form.conditions.hasVatNumber ?? undefined,
      ibans: form.conditions.ibans.length ? form.conditions.ibans : undefined,
      name: form.conditions.name.value ? form.conditions.name : undefined,
      vatPaymentFrequencies: form.conditions.vatPaymentFrequencies.length
        ? form.conditions.vatPaymentFrequencies
        : undefined,
    };

    if (form.isSplit) {
      if (form.splitLines.length < 2) {
        return SPLIT_LINE_ERRORS.MIN_LINES;
      }
      for (const line of form.splitLines) {
        if (!line.categoryCode) return SPLIT_LINE_ERRORS.CATEGORY;
        if (!line.vatCategoryCode) return SPLIT_LINE_ERRORS.VAT_CATEGORY;
        if (!line.amount) return SPLIT_LINE_ERRORS.AMOUNT;
      }
      return {
        description: form.description,
        enabled: form.enabled,
        priority: form.priority,
        verify: form.verify,
        splitLines: form.splitLines.map((line) => ({
          categoryCode: categoryCodeCodec.encode(line.categoryCode) as any,
          vatCategoryCode: vatCategoryCodeCodec.encode(
            line.vatCategoryCode
          ) as any,
          amount: line.amount,
        })),
        conditions,
      };
    }

    const categoryCode = categoryCodeCodec.encode(form.categoryCode);
    if (!categoryCode) {
      return `Invalid category code ${form.categoryCode}`;
    }
    const vatCategoryCode = vatCategoryCodeCodec.encode(form.vatCategoryCode);
    if (!vatCategoryCode) {
      return `Invalid VAT category code ${form.vatCategoryCode}`;
    }

    return {
      description: form.description,
      enabled: form.enabled,
      priority: form.priority,
      categoryCode,
      vatCategoryCode,
      // Only carry the VAT ID when the rule targets a single recipient; clear it
      // otherwise so a broadly-scoped rule never stamps one across recipients.
      recipientVatId: ruleFormTargetsSingleRecipient(form.conditions)
        ? recipientVatIdForUpsert(form.vatCategoryCode, form.recipientVatId)
        : null,
      verify: form.verify,
      conditions,
    };
  }, [form]);

  return {
    form,
    setForm,
    formControls,
    getRule,
  };
}

function getRuleFormState(rule?: AutocategorizationRuleFragment) {
  // Use splitLines presence to determine split mode — recurring payment rules have
  // isSplit=true but no splitLines, and should not activate the split UI.
  const isSplit = (rule?.splitLines?.length ?? 0) >= 2;
  return {
    description: rule?.description ?? "",
    enabled: rule?.enabled ?? true,
    priority: rule?.priority ?? 0,
    isSplit,
    splitLines: (rule?.splitLines ?? []).map((line) => ({
      categoryCode: categoryCodeCodec.decode(line.categoryCode as any) ?? "",
      vatCategoryCode:
        vatCategoryCodeCodec.decode(line.vatCategoryCode as any) ?? "",
      amount: line.amount,
    })) as RuleSplitLineForm[],
    categoryCode: isSplit
      ? ""
      : categoryCodeCodec.decode(rule?.categoryCode) ?? "",
    vatCategoryCode: isSplit
      ? ""
      : vatCategoryCodeCodec.decode(rule?.vatCategoryCode) ?? "",
    recipientVatId: isSplit ? "" : rule?.recipientVatId ?? "",
    verify: rule?.verify ?? false,
    conditions: {
      accountIds: rule?.conditions?.accountIds ?? [],
      hasEmployees: rule?.conditions?.hasEmployees ?? undefined,
      hasInvoices: rule?.conditions?.hasInvoices ?? undefined,
      hasVatNumber: rule?.conditions?.hasVatNumber ?? undefined,
      ibans: rule?.conditions?.ibans ?? [],
      vatPaymentFrequencies: rule?.conditions?.vatPaymentFrequencies ?? [],
      direction: decodeDirectionCondition(rule?.conditions.amount),
      amount: decodeAmountCondition(rule?.conditions.amount),
      description: {
        op: rule?.conditions.description?.op || RuleConditionStringOp.EQ,
        value: rule?.conditions.description?.value || "",
      },
      name: {
        op: rule?.conditions.name?.op || RuleConditionStringOp.EQ,
        value: rule?.conditions.name?.value || "",
      },
    },
  };
}

function decodeDirectionCondition(
  amount?: AutocategorizationRuleFragment["conditions"]["amount"]
): Direction | undefined {
  if (!amount) {
    return;
  }

  if (amount.value === 0) {
    return amount.op === RuleConditionNumberOp.LT
      ? Direction.OUTGOING
      : Direction.INCOMING;
  }

  return amount.value > 0 ? Direction.INCOMING : Direction.OUTGOING;
}

function decodeAmountCondition(
  amount?: AutocategorizationRuleFragment["conditions"]["amount"]
) {
  if (!amount) {
    return {
      op: RuleConditionNumberOp.EQ,
      value: undefined,
    };
  }

  const op =
    amount.value < 0 ? INVERSE_CONDITION_NUMBER_OP[amount.op] : amount.op;
  const valueInEur = Math.abs(amount.value) / 100;

  return {
    op,
    value: valueInEur ? valueInEur.toString() : undefined,
  };
}
